/** The CAS books of accounts (M11-01), viewable and exportable for any period. */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileDown, FileText } from 'lucide-react';
import { api } from '../../lib/api';
import { amount } from '../../lib/money';
import { downloadFile, openFile } from '../../lib/files';
import { manilaToday } from '../../lib/queries';
import { Alert, Button, Card, EmptyState, FilterBar, PageHeader, Skeleton, filterClass } from '../../components/ui';

interface Column { key: string; label: string; money?: boolean }
interface Book { title: string; from: string; to: string; columns: Column[]; rows: Record<string, string>[]; sections?: { title: string; opening?: string; closing?: string; rows: Record<string, string>[] }[]; totals: Record<string, string>; company: { name: string; tin: string; address: string } }

export const BOOKS: Record<string, string> = {
  'general-journal': 'General Journal',
  'general-ledger': 'General Ledger',
  'cash-receipts': 'Cash Receipts Book',
  'cash-disbursements': 'Cash Disbursements Book',
  'sales-journal': 'Sales Journal',
  'purchase-journal': 'Purchase Journal',
  'inventory-book': 'Inventory Book (Stock Ledger)',
};

function BookTable({ columns, rows, totals }: { columns: Column[]; rows: Record<string, string>[]; totals?: Record<string, string> }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead><tr className="border-b border-slate-200 dark:border-slate-800">{columns.map((c) => <th key={c.key} className={`pb-2 pr-3 font-semibold uppercase tracking-wide text-slate-500 ${c.money ? 'text-right' : ''}`}>{c.label}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((r, i) => <tr key={i}>{columns.map((c) => <td key={c.key} className={`py-1.5 pr-3 ${c.money ? 'text-right tabular-nums' : ''} ${r.serial?.includes('CANCELLED') || r.number?.includes('VOIDED') ? 'text-slate-400' : ''}`}>{c.money && r[c.key] ? amount(r[c.key]) : r[c.key]}</td>)}</tr>)}
        </tbody>
        {totals && Object.keys(totals).length > 0 && (
          <tfoot><tr className="border-t-2 border-slate-300 font-bold dark:border-slate-700">{columns.map((c, i) => <td key={c.key} className={`pt-2 pr-3 ${c.money ? 'text-right tabular-nums' : ''}`}>{i === 0 ? 'Total' : c.money ? amount(totals[c.key] ?? '', true) : ''}</td>)}</tr></tfoot>
        )}
      </table>
    </div>
  );
}

export function BooksPage(): JSX.Element {
  const { book = 'general-journal' } = useParams();
  const today = manilaToday();
  const [from, setFrom] = useState(`${today.slice(0, 7)}-01`);
  const [to, setTo] = useState(today);
  const [error, setError] = useState<string | null>(null);
  const data = useQuery({ queryKey: ['book', book, from, to], queryFn: () => api<Book>(`/books/${book}?from=${from}&to=${to}`), enabled: from <= to });
  const exp = useMutation({ mutationFn: (f: 'pdf' | 'csv' | 'xlsx') => (f === 'pdf' ? openFile(`/books/${book}?from=${from}&to=${to}&format=pdf`) : downloadFile(`/books/${book}?from=${from}&to=${to}&format=${f}`)), onError: (e) => setError((e as Error).message) });
  return (
    <>
      <PageHeader
        title={BOOKS[book] ?? 'Books'}
        description="Registered books of accounts, produced from posted entries. Printed copies carry the company header, page numbers and a system-generated footer."
        actions={<><Button variant="secondary" onClick={() => exp.mutate('pdf')}><FileText aria-hidden="true" className="h-4 w-4" /> PDF</Button><Button variant="secondary" onClick={() => exp.mutate('xlsx')}><FileDown aria-hidden="true" className="h-4 w-4" /> Excel</Button><Button variant="secondary" onClick={() => exp.mutate('csv')}><FileDown aria-hidden="true" className="h-4 w-4" /> CSV</Button></>}
      />
      <FilterBar>
        <label className="text-sm text-slate-600 dark:text-slate-300">From <input type="date" className={`${filterClass} ml-1`} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-sm text-slate-600 dark:text-slate-300">To <input type="date" className={`${filterClass} ml-1`} value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </FilterBar>
      {error && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      <Card>
        {data.isLoading && <Skeleton className="h-64 w-full" />}
        {data.data && (
          <>
            <div className="mb-4 text-xs text-slate-500"><p className="font-semibold text-slate-800 dark:text-slate-100">{data.data.company.name}</p><p>{data.data.company.tin} · {data.data.company.address}</p></div>
            {data.data.sections ? (
              data.data.sections.length === 0 ? <EmptyState title="No postings in this period" /> : (
                <div className="space-y-6">
                  {data.data.sections.map((s) => (
                    <section key={s.title}>
                      <div className="mb-1 flex flex-wrap justify-between gap-2 text-sm"><h3 className="font-semibold">{s.title}</h3><span className="text-xs text-slate-500">Opening {amount(s.opening ?? '0')} · Closing {amount(s.closing ?? '0')}</span></div>
                      <BookTable columns={data.data!.columns} rows={s.rows} />
                    </section>
                  ))}
                </div>
              )
            ) : data.data.rows.length === 0 ? <EmptyState title="No entries in this period" /> : <BookTable columns={data.data.columns} rows={data.data.rows} totals={data.data.totals} />}
          </>
        )}
      </Card>
    </>
  );
}
